/**
 * FERP Portal - Primitive Documentation Modal
 *
 * Living documentation viewer for all 25 FERP primitives.
 * Tabbed interface showing overview, attributes, usage, examples, etc.
 *
 * PATTERN: DRY - One component for all primitives
 */

import {
  Modal,
  Tabs,
  Stack,
  Text,
  Title,
  Badge,
  Table,
  Code,
  List,
  Paper,
  Group,
  Divider,
  Alert,
} from '@mantine/core';
import {
  IconInfoCircle,
  IconList,
  IconBook,
  IconCode,
  IconNetwork,
  IconBulb,
} from '@tabler/icons-react';
import type { PrimitiveType } from '@/config/primitive-metadata';
import { useMetadataRepository } from '@/repositories';

// ============================================================================
// TYPES
// ============================================================================

interface PrimitiveDocumentationModalProps {
  opened: boolean;
  onClose: () => void;
  primitiveType: PrimitiveType;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PrimitiveDocumentationModal({
  opened,
  onClose,
  primitiveType,
}: PrimitiveDocumentationModalProps) {
  const metadataRepo = useMetadataRepository();

  // Capitalize primitive name to match backend format (e.g., "workflow" → "Workflow")
  const primitiveName = primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1);
  const { data: primitiveMetadata, loading, error } = metadataRepo.usePrimitive(primitiveName);

  if (loading) {
    return (
      <Modal opened={opened} onClose={onClose} title="Loading Documentation..." size="lg">
        <Text c="dimmed">Loading documentation for {primitiveType}...</Text>
      </Modal>
    );
  }

  if (error || !primitiveMetadata) {
    return (
      <Modal opened={opened} onClose={onClose} title="Documentation Not Available" size="lg">
        <Text c="dimmed">
          {error ? `Error loading documentation: ${error.message}` : `No documentation available for ${primitiveType} yet.`}
        </Text>
      </Modal>
    );
  }

  // Parse metadata JSON if available
  let parsedMetadata: any = null;
  if (primitiveMetadata.metadata) {
    try {
      parsedMetadata = JSON.parse(primitiveMetadata.metadata);
    } catch (e) {
      console.warn('Failed to parse primitive metadata JSON:', e);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconBook size={20} />
          <Title order={3}>
            {primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1)} Documentation
          </Title>
        </Group>
      }
      size="xl"
      // @ts-expect-error Type assignment
      scrollAreaComponent="div"
    >
      <Tabs defaultValue="overview" variant="pills">
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="attributes" leftSection={<IconList size={16} />}>
            Attributes
          </Tabs.Tab>
          <Tabs.Tab value="usage" leftSection={<IconBook size={16} />}>
            Usage
          </Tabs.Tab>
          <Tabs.Tab value="examples" leftSection={<IconCode size={16} />}>
            Examples
          </Tabs.Tab>
          <Tabs.Tab value="relationships" leftSection={<IconNetwork size={16} />}>
            Relationships
          </Tabs.Tab>
          <Tabs.Tab value="best-practices" leftSection={<IconBulb size={16} />}>
            Best Practices
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="md">
            <div>
              <Text size="sm" fw={600} c="dimmed">
                Name
              </Text>
              <Text>{primitiveMetadata.name}</Text>
            </div>

            <Divider />

            <div>
              <Text size="sm" fw={600} c="dimmed">
                Description
              </Text>
              <Text>{primitiveMetadata.description || 'No description available'}</Text>
            </div>

            <Divider />

            <div>
              <Text size="sm" fw={600} c="dimmed">
                Phase
              </Text>
              <Badge size="lg" variant="light">
                {primitiveMetadata.phase}
              </Badge>
            </div>

            <Divider />

            <div>
              <Text size="sm" fw={600} c="dimmed">
                Life Critical
              </Text>
              <Badge size="lg" c={primitiveMetadata.life_critical ? 'red' : 'gray'}>
                {primitiveMetadata.life_critical ? 'Yes - Safety Critical' : 'No'}
              </Badge>
            </div>

            {parsedMetadata?.overview && (
              <>
                <Divider />
                <div>
                  <Text size="sm" fw={600} c="dimmed">
                    Use Case
                  </Text>
                  <Text>{parsedMetadata.overview.useCase}</Text>
                </div>
              </>
            )}

            {parsedMetadata?.overview?.lifecycle && (
              <>
                <Divider />
                <div>
                  <Text size="sm" fw={600} c="dimmed">
                    Lifecycle
                  </Text>
                  <Text>{parsedMetadata.overview.lifecycle}</Text>
                </div>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Attributes Tab */}
        <Tabs.Panel value="attributes" pt="md">
          <Stack gap="lg">
            {/* All Fields */}
            <div>
              <Group gap="xs" mb="sm">
                <Title order={5}>Fields</Title>
                <Badge size="sm" variant="light">
                  {primitiveMetadata.fields.length} fields
                </Badge>
              </Group>
              <Text size="sm" c="dimmed" mb="md">
                All attributes for {primitiveMetadata.name}
              </Text>
              <Paper withBorder p="md">
                <List spacing="xs" size="sm">
                  {primitiveMetadata.fields.map((field: any, index: any) => (
                    <List.Item key={index}>
                      <Code>{field}</Code>
                    </List.Item>
                  ))}
                </List>
              </Paper>
            </div>

            {parsedMetadata?.attributes && (
              <>
                <Divider />
                <div>
                  <Group gap="xs" mb="sm">
                    <Title order={5}>Detailed Attributes</Title>
                    <Badge size="sm" variant="light" c="blue">
                      Extended metadata
                    </Badge>
                  </Group>
                  {parsedMetadata.attributes.common && (
                    <AttributeTable attributes={parsedMetadata.attributes.common} />
                  )}
                  {parsedMetadata.attributes.specific && parsedMetadata.attributes.specific.length > 0 && (
                    <>
                      <Divider my="md" />
                      <Text size="sm" fw={600} mb="sm">
                        Primitive-Specific Attributes
                      </Text>
                      <AttributeTable attributes={parsedMetadata.attributes.specific} />
                    </>
                  )}
                </div>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Usage Tab */}
        <Tabs.Panel value="usage" pt="md">
          <Stack gap="md">
            <Paper withBorder p="md" bg="blue.0">
              <Text size="sm" fw={600} mb="xs">
                GraphQL Query
              </Text>
              <Code block>{primitiveMetadata.graphql_query}</Code>
            </Paper>

            <Paper withBorder p="md" bg="green.0">
              <Text size="sm" fw={600} mb="xs">
                GraphQL Mutation Prefix
              </Text>
              <Code block>{primitiveMetadata.graphql_mutation_prefix}</Code>
            </Paper>

            {parsedMetadata?.usage && (
              <>
                <Paper withBorder p="md" bg="purple.0">
                  <Text size="sm" fw={600} mb="xs">
                    Create
                  </Text>
                  <Text size="sm">{parsedMetadata.usage.create}</Text>
                </Paper>

                <Paper withBorder p="md" bg="teal.0">
                  <Text size="sm" fw={600} mb="xs">
                    Edit
                  </Text>
                  <Text size="sm">{parsedMetadata.usage.edit}</Text>
                </Paper>

                <Paper withBorder p="md" bg="orange.0">
                  <Text size="sm" fw={600} mb="xs">
                    Archive
                  </Text>
                  <Text size="sm">{parsedMetadata.usage.archive}</Text>
                </Paper>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Examples Tab */}
        <Tabs.Panel value="examples" pt="md">
          <Stack gap="lg">
            {parsedMetadata?.examples && parsedMetadata.examples.length > 0 ? (
              parsedMetadata.examples.map((example: any, index: number) => (
                <Paper key={index} withBorder p="md">
                  <Title order={5} mb="xs">
                    {example.title}
                  </Title>
                  <Text size="sm" c="dimmed" mb="md">
                    {example.description}
                  </Text>
                  {example.code && (
                    <Code block style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {example.code}
                    </Code>
                  )}
                </Paper>
              ))
            ) : (
              <Text c="dimmed" size="sm">
                No examples available yet. Examples can be added to the backend metadata field.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Relationships Tab */}
        <Tabs.Panel value="relationships" pt="md">
          <Stack gap="md">
            {parsedMetadata?.relationships ? (
              <>
                <div>
                  <Group gap="xs" mb="sm">
                    <Text size="sm" fw={600}>
                      Depends On
                    </Text>
                    <Badge size="sm" variant="light" c="orange">
                      {parsedMetadata.relationships.dependsOn?.length || 0}
                    </Badge>
                  </Group>
                  {parsedMetadata.relationships.dependsOn && parsedMetadata.relationships.dependsOn.length > 0 ? (
                    <Group gap="xs">
                      {parsedMetadata.relationships.dependsOn.map((dep: string) => (
                        <Badge key={dep} variant="outline" c="orange">
                          {dep}
                        </Badge>
                      ))}
                    </Group>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No dependencies
                    </Text>
                  )}
                </div>

                <Divider />

                <div>
                  <Group gap="xs" mb="sm">
                    <Text size="sm" fw={600}>
                      Used By
                    </Text>
                    <Badge size="sm" variant="light" c="green">
                      {parsedMetadata.relationships.usedBy?.length || 0}
                    </Badge>
                  </Group>
                  {parsedMetadata.relationships.usedBy && parsedMetadata.relationships.usedBy.length > 0 ? (
                    <Group gap="xs">
                      {parsedMetadata.relationships.usedBy.map((user: string) => (
                        <Badge key={user} variant="outline" c="green">
                          {user}
                        </Badge>
                      ))}
                    </Group>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Not used by other primitives
                    </Text>
                  )}
                </div>
              </>
            ) : (
              <Text c="dimmed" size="sm">
                Relationship metadata not yet populated in backend. This can be added to the metadata JSON field.
              </Text>
            )}

            <Alert icon={<IconInfoCircle size={16} />} c="blue" title="Relationship Graph" mt="md">
              <Text size="sm">
                This shows direct relationships. View the full dependency graph in the Architecture
                documentation.
              </Text>
            </Alert>
          </Stack>
        </Tabs.Panel>

        {/* Best Practices Tab */}
        <Tabs.Panel value="best-practices" pt="md">
          <Stack gap="md">
            <Alert icon={<IconBulb size={16} />} c="yellow" title="Best Practices">
              Follow these guidelines for safe and efficient use
            </Alert>
            {parsedMetadata?.bestPractices && parsedMetadata.bestPractices.length > 0 ? (
              <List spacing="sm" size="sm">
                {parsedMetadata.bestPractices.map((practice: string, index: number) => (
                  <List.Item key={index}>{practice}</List.Item>
                ))}
              </List>
            ) : (
              <Text c="dimmed" size="sm">
                No best practices documented yet. Best practices can be added to the backend metadata field.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface PrimitiveAttribute {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
  defaultValue?: string;
}

function AttributeTable({ attributes }: { attributes: PrimitiveAttribute[] }) {
  return (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Type</Table.Th>
          <Table.Th>Required</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th>Example / Default</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {attributes.map((attr) => (
          <Table.Tr key={attr.name}>
            <Table.Td>
              <Code>{attr.name}</Code>
            </Table.Td>
            <Table.Td>
              <Badge size="sm" variant="light" c="blue">
                {attr.type}
              </Badge>
            </Table.Td>
            <Table.Td>
              {attr.required ? (
                <Badge size="sm" c="red">
                  Required
                </Badge>
              ) : (
                <Badge size="sm" c="gray" variant="outline">
                  Optional
                </Badge>
              )}
            </Table.Td>
            <Table.Td>
              <Text size="sm">{attr.description}</Text>
            </Table.Td>
            <Table.Td>
              {attr.example && (
                <Code style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--mantine-font-size-xs)' }}>
                  {attr.example}
                </Code>
              )}
              {attr.defaultValue && !attr.example && (
                <Text size="xs" c="dimmed">
                  Default: {attr.defaultValue}
                </Text>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
